import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export default function Login() {
  const { login, error, errorDetail, loading, client, redirectUri, canonicalUrl, notice, deployment, resetSignIn } =
    useAuth();

  const [handle, setHandle] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (event) => {
    event.preventDefault();

    if (!handle.trim() || submitting) return;
    setSubmitting(true);
    await login(handle);
    setSubmitting(false);
  };

  return (
    <div className="app-container">
      <header className="navbar">
        <div className="navbar-logo">Follower</div>
        <div className="navbar-actions">
          <Link className="button-link" to="/preview">
            Preview
          </Link>
        </div>
      </header>

      <main className="main-container">
        <div className="login-page">
          <div className="login-content">
            <h1>Follower</h1>
            <p>
              Follow an account on every appview it has joined, not just the one you are looking at.
            </p>
            <p className="prose">
              Sign in with your own AT Protocol account. Follower reads the other account&rsquo;s
              public repository to see which appviews it holds a profile record in, then writes the
              matching follow record into your repository. You approve every write.
            </p>

            <form onSubmit={submit}>
              <label className="field" htmlFor="login-handle">
                <span className="field-label">Your handle</span>
                <input
                  id="login-handle"
                  className="input"
                  type="text"
                  name="handle"
                  autoComplete="username"
                  placeholder="you.bsky.social"
                  value={handle}
                  onChange={(event) => setHandle(event.target.value)}
                  disabled={loading || submitting}
                  spellCheck="false"
                  autoCapitalize="none"
                />
              </label>
              <button type="submit" disabled={loading || submitting || !client || !redirectUri}>
                {submitting ? 'Opening sign-in…' : 'Login with Bluesky'}
              </button>
            </form>

            {deployment?.stale && (
              <div className="notice notice-info" role="status">
                <strong>A newer build is deployed.</strong> This tab is running{' '}
                <code>{deployment.running}</code>; the server has <code>{deployment.deployed}</code>.
                Reload to pick it up before signing in.
                <div className="notice-actions">
                  <button type="button" onClick={() => window.location.reload()}>
                    Reload
                  </button>
                </div>
              </div>
            )}

            {notice && !error && (
              <div className="notice notice-info" role="status">
                {notice}
              </div>
            )}

            {(error || !client || !redirectUri) && (
              <div className="notice notice-error" role="alert">
                {error ||
                  (loading
                    ? 'Starting the OAuth client…'
                    : canonicalUrl
                      ? `Sign in from ${canonicalUrl}. This host is not a registered redirect target for OAuth, so sign-in here would fail.`
                      : 'Sign-in is not available: OAuth client metadata did not load.')}
                {errorDetail && <p className="notice-detail">Cause: {errorDetail}</p>}
                <div className="notice-actions">
                  <button type="button" className="button-secondary" onClick={resetSignIn}>
                    Reset local sign-in state
                  </button>
                </div>
              </div>
            )}

            <p className="footnote">
              We use official Bluesky/ATProto OAuth to securely process your login. Follower never
              sees or stores your password, and it never writes anything you did not ask for.
            </p>

            <p className="footnote">
              <Link to="/preview">Look at the read-only preview first</Link> — it scans public
              repositories without signing in.
            </p>

            <p className="footnote">
              Stuck on an old session?{' '}
              <button type="button" className="button-link" onClick={resetSignIn}>
                Reset local sign-in state
              </button>
            </p>
          </div>
        </div>
      </main>

      <footer className="footer">Reads public repositories · writes only follow records you approve</footer>
    </div>
  );
}
